import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { getOrderBySession, urlDocument } from '../services/api';
import { useLang } from '../i18n/LanguageContext';

export default function Success() {
  const { clearCart } = useCart();
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Questionnaire souvenir : rempli par défaut, vierge si la case est cochée.
  const [questionnaireVierge, setQuestionnaireVierge] = useState(false);


  // Use a ref to ensure clearCart is only called once per mount
  const cartClearedRef = useRef(false);

  useEffect(() => {
    // 1. Clear cart if not already cleared
    if (!cartClearedRef.current) {
      clearCart();
      cartClearedRef.current = true;
    }

    // 2. Fetch order details if session_id is present
    const fetchOrder = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Poll for order status (webhook might take a few seconds)
        let attempts = 0;
        const maxAttempts = 10;

        const poll = async () => {
          try {
            const data = await getOrderBySession(sessionId);

            // If we have tracking info or max attempts reached, set data
            if (data.trackingNumber || attempts >= maxAttempts) {
              setOrderData(data);
              setLoading(false);
            } else {
              // Wait and retry
              attempts++;
              setTimeout(poll, 2000); // Retry every 2 seconds
            }
          } catch (err) {
            console.error('Error fetching order:', err);

            setLoading(false);
          }
        };

        poll();
      } catch (err) {
        console.error('Error in fetchOrder:', err);
        setLoading(false);
      }
    };

    fetchOrder();
  }, [sessionId, clearCart]); // Dependencies are fine now with the ref guard

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">{t('succes.chargement.titre')}</h2>
          <p className="mt-2 text-sm text-gray-600">{t('succes.chargement.texte')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg space-y-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-900/5 sm:p-10">
        <div className="text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <span className="text-4xl">🎉</span>
          </div>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">{t('succes.entete.titre')}</h1>

          {orderData?.orderNumber && (
            <p className="mt-2 text-lg font-medium text-gray-600">
              {t('succes.entete.numeroLabel')} <span className="font-bold text-gray-900">{orderData.orderNumber}</span>
            </p>
          )}

          <p className="mt-4 text-base text-gray-500">
            {t('succes.entete.merci')}
          </p>

          {/* Sans ce bloc, une session illisible (lien rouvert des semaines
              plus tard, identifiant périmé) donnait une page amputée en
              silence : l'acheteur voyait « Commande Réussie ! » sans numéro,
              sans guide et sans facture, et sans savoir pourquoi. */}
          {!orderData?.orderNumber && (
            <p className="mt-4 rounded-xl bg-amber-50 p-4 text-left text-sm text-amber-900 ring-1 ring-amber-200">
              {t('succes.introuvable.texte')}
            </p>
          )}
        </div>

        {/* Le guide de règles, téléchargeable tout de suite.
            Il contient la réponse de la question subsidiaire : le serveur ne le
            remet qu'au couple (numéro de commande, e-mail de l'achat), jamais
            depuis une URL devinable. */}
        {orderData?.orderNumber && orderData?.customerEmail && (
          <div className="rounded-xl bg-[#F7F4EE] p-6 ring-1 ring-[#4AA294]/30">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📖</span>
              <h3 className="text-lg font-semibold text-[#17453E]">{t('succes.guide.titre')}</h3>
            </div>
            <p className="mt-2 text-sm text-[#33403B]/80">{t('succes.guide.texte')}</p>
            <a
              href={`${import.meta.env.VITE_API_URL || ''}/guide?numero=${encodeURIComponent(orderData.orderNumber)}&email=${encodeURIComponent(orderData.customerEmail)}&langue=${lang}`}
              data-cta="succes:guide"
              className="mt-4 inline-block rounded-lg bg-[#2F7E72] px-5 py-2.5 text-sm font-bold text-white no-underline transition-all hover:bg-[#17453E]"
            >
              {t('succes.guide.bouton')}
            </a>
            <p className="mt-2 text-xs text-[#33403B]/60">{t('succes.guide.avertissement')}</p>
          </div>
        )}

        {/* Le questionnaire souvenir (rempli ou vierge). Son verso porte lui
            aussi la réponse de la question subsidiaire : il passe par la même
            garde serveur (numéro de commande + e-mail) que le guide. */}
        {orderData?.orderNumber && orderData?.customerEmail && (
          <div className="rounded-xl bg-[#F7F4EE] p-6 ring-1 ring-[#4AA294]/30">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📝</span>
              <h3 className="text-lg font-semibold text-[#17453E]">
                {t('succes.questionnaire.titre')}
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#33403B]/80">{t('succes.questionnaire.texte')}</p>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-[#33403B]">
              <input
                type="checkbox"
                checked={questionnaireVierge}
                onChange={(e) => setQuestionnaireVierge(e.target.checked)}
                className="h-4 w-4 rounded accent-[#2F7E72]"
              />
              {t('succes.questionnaire.vierge')}
            </label>
            <a
              href={`${import.meta.env.VITE_API_URL || ''}/guide?numero=${encodeURIComponent(orderData.orderNumber)}&email=${encodeURIComponent(orderData.customerEmail)}&langue=${lang}&document=${questionnaireVierge ? 'questionnaire-vierge' : 'questionnaire'}`}
              data-cta="succes:questionnaire"
              className="mt-4 inline-block rounded-lg bg-[#2F7E72] px-5 py-2.5 text-sm font-bold text-white no-underline transition-all hover:bg-[#17453E]"
            >
              {t('succes.questionnaire.bouton')}
            </a>
            <p className="mt-2 text-xs text-[#33403B]/60">
              {t('succes.questionnaire.avertissement')}
            </p>
          </div>
        )}

        {/* La facture de la commande : émise par le site depuis le 23/09/2026
            (lien signé), ou la facture Stripe d'une session plus ancienne.
            Stripe envoie en plus son reçu par e-mail — un reçu, pas une facture. */}
        {(orderData?.factureChemin || orderData?.invoiceUrl) && (
          <div className="rounded-xl bg-gray-50 p-6 ring-1 ring-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🧾</span>
              <h3 className="text-lg font-semibold text-gray-900">{t('succes.facture.titre')}</h3>
            </div>
            <p className="mt-2 text-sm text-gray-600">{t('succes.facture.texte')}</p>
            {orderData.factureNumero && (
              <p className="mt-1 text-sm font-medium text-gray-800">{orderData.factureNumero}</p>
            )}
            <a
              href={orderData.factureChemin ? urlDocument(orderData.factureChemin) : orderData.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-cta="succes:facture"
              className="mt-4 inline-block rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-bold text-white no-underline transition-all hover:bg-black"
            >
              {t('succes.facture.bouton')}
            </a>
          </div>
        )}

        {orderData?.trackingUrl ? (
          <div className="rounded-xl bg-blue-50 p-6 ring-1 ring-blue-100">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📦</span>
              <h3 className="text-lg font-semibold text-blue-900">{t('succes.suivi.titre')}</h3>
            </div>
            <p className="mt-2 text-sm text-blue-700">{t('succes.suivi.etiquette')}</p>

            {orderData.trackingNumber && (
              <p className="mt-1 text-sm font-medium text-blue-800">
                {t('succes.suivi.numeroLabel')} <span className="font-mono">{orderData.trackingNumber}</span>
              </p>
            )}

            <div className="mt-4">
              <a
                href={orderData.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                {t('succes.suivi.bouton')}
              </a>
            </div>
            <p className="mt-3 text-xs text-blue-600">
              {t('succes.suivi.astuce')}
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-yellow-50 p-6 ring-1 ring-yellow-100">
            <p className="text-sm text-yellow-800">
              {t('succes.attente.texte')}
            </p>
          </div>
        )}

        {/* Review Invitation Card */}
        <div className="rounded-xl bg-amber-50 p-6 ring-1 ring-amber-100">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">⭐</span>
            <h3 className="text-lg font-semibold text-amber-900">{t('succes.avis.titre')}</h3>
          </div>
          <p className="text-sm text-amber-700 mb-4">
            {t('succes.avis.texte')}
          </p>
          <Link
            to="/#reviews"
            className="inline-flex items-center justify-center rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 transition-colors"
          >
            {t('succes.avis.bouton')}
          </Link>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Link
            to="/profile/orders"
            className="inline-flex w-full items-center justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {t('succes.actions.commandes')}
          </Link>
          <Link
            to="/shop"
            className="inline-flex w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {t('succes.actions.boutique')}
          </Link>
        </div>
      </div>
    </div>
  );
}
